import { type ReactNode } from "react";
import { usePathname, useSearch as useSearchParams } from "../../router";
import { ArrowRight, Archive } from "../../icons";
import { ContextMenu, type ContextMenuItem } from "../../context-menu";
import {
  STATUS_LABEL,
  type ScopedTask,
  type TaskStatus,
} from "../../types/projects";
import { StatusIcon } from "./status-icon";

type TaskActionScope = { projectSlug: string; sectionSlug: string };

export interface TaskContextMenuActions {
  /** Move a task to a new status. Reuses the shell's (ungated) UI action. */
  setStatus: (
    scope: TaskActionScope,
    taskId: number,
    status: TaskStatus,
  ) => Promise<unknown>;
  /** Optional — when provided, an "Archive" item is appended. */
  archiveTask?: (scope: TaskActionScope, taskId: number) => Promise<unknown>;
}

// Everyday lifecycle states surfaced in the menu. backlog/cancelled stay
// reachable from the task peek; the menu keeps the common path short.
const MENU_STATUSES: TaskStatus[] = ["todo", "in_progress", "review", "done"];

/**
 * Right-click context menu for a task row/card. Wraps `children` (the visible
 * card) in the shared {@link ContextMenu} primitive and builds the items array
 * from the task + injected server actions — the same pattern as
 * {@link ProjectCardContextMenu}.
 *
 * Status changes route through the shell's `setStatus` UI action, which is
 * deliberately outside the agent ship/verify gate (CLAUDE.md carve-out for the
 * human UI) — identical to the existing drag-to-column behavior on the board.
 */
export function TaskContextMenu({
  task,
  actions,
  children,
}: {
  task: ScopedTask;
  actions: TaskContextMenuActions;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const search = useSearchParams();

  // Mirror TaskCard's click behavior: append ?task=<id> so TaskPeekHost
  // (mounted in the root layout) opens the drawer on the current page.
  const next = new URLSearchParams(search.toString());
  next.set("task", String(task.id));
  const openHref = `${pathname}?${next.toString()}`;

  const taskScope: TaskActionScope = {
    projectSlug: task.project_slug,
    sectionSlug: task.section_slug,
  };

  const items: ContextMenuItem[] = [
    {
      label: "Open",
      href: openHref,
      icon: <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />,
    },
    { type: "separator" },
    { type: "label", label: "Move to" },
    ...MENU_STATUSES.map(
      (status): ContextMenuItem => ({
        label: STATUS_LABEL[status],
        icon: <StatusIcon status={status} />,
        disabled: task.status === status,
        onSelect: () => {
          void actions.setStatus(taskScope, task.id, status);
        },
      }),
    ),
  ];

  if (actions.archiveTask) {
    const archive = actions.archiveTask;
    items.push(
      { type: "separator" },
      {
        label: "Archive",
        destructive: true,
        icon: <Archive className="h-3.5 w-3.5" strokeWidth={1.75} />,
        onSelect: () => {
          void archive(taskScope, task.id);
        },
      },
    );
  }

  return <ContextMenu items={items}>{children}</ContextMenu>;
}
